// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { MetricChartEntityRegistry, MetricType } from '@lfx/key-projects/entities/metric-chart-entity.registry';
import { API_CONSTANTS, TIME_RANGE_NAMES } from '@lfx/key-projects/key-projects.constants';

/**
 * Service for metric chart data operations
 * Single Responsibility: Handle metric chart data fetching and processing
 */
@Injectable({
  providedIn: 'root',
})
export class MetricChartDataService {
  private readonly metricChartRegistry = inject(MetricChartEntityRegistry);

  /**
   * Get table columns for metric chart offcanvas
   */
  getMetricChartColumns(metricType?: string): Observable<Column[]> {
    return new Observable<Column[]>(observer => {
      setTimeout(() => {
        try {
          const columns = this.metricChartRegistry.getColumns(metricType as MetricType);
          observer.next(columns);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      }, API_CONSTANTS.METRIC_CHART_COLUMN_DELAY);
    }).pipe(catchError(() => of([])));
  }

  /**
   * Get table data for metric chart offcanvas
   */
  getMetricChartData(metricType?: string, projectSlug?: string): Observable<DataRow[]> {
    if (this.metricChartRegistry.isSupported(metricType)) {
      return this.metricChartRegistry
        .fetchData(metricType as MetricType, {
          projectSlug,
          timeRangeName: TIME_RANGE_NAMES.LAST_24_MONTHS,
        })
        .pipe(catchError(() => of([])));
    }

    return this.metricChartRegistry.fetchData(undefined, {}).pipe(
      switchMap(data =>
        new Observable<DataRow[]>(observer => {
          setTimeout(() => {
            observer.next(data);
            observer.complete();
          }, API_CONSTANTS.METRIC_CHART_DATA_DELAY);
        })
      ),
      catchError(() => of([]))
    );
  }

  /**
   * Get summary table data for metric chart offcanvas
   */
  getSummaryTableData(
    metricType?: string,
    projectSlug?: string
  ): Observable<{ columns: Column[]; data: DataRow[] }> {
    const params = {
      projectSlug,
      timeRangeName: TIME_RANGE_NAMES.LAST_24_MONTHS,
    };

    return this.metricChartRegistry.getSummaryTableData(metricType as MetricType, params).pipe(
      switchMap(summaryData =>
        new Observable<{ columns: Column[]; data: DataRow[] }>(observer => {
          setTimeout(() => {
            observer.next(summaryData);
            observer.complete();
          }, API_CONSTANTS.SUMMARY_TABLE_DELAY);
        })
      ),
      catchError(() => {
        return this.metricChartRegistry.getSummaryTableData(undefined, {}).pipe(
          map(data => ({ columns: data.columns, data: [] }))
        );
      })
    );
  }
}

